import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { Alert } from "react-bootstrap";
import useVideoContext from "hooks/livekit/useVideoContext/useVideoContext";
import useDevices from "hooks/livekit/useDevices/useDevices";
import useDeviceReadiness from "hooks/livekit/useDeviceReadiness/useDeviceReadiness";
import useMediaStreamTrack from "hooks/livekit/useMediaStreamTrack/useMediaStreamTrack";
import { SELECTED_AUDIO_INPUT_KEY } from "config/livekit";
import { useSessionContext } from "hooks/useSession";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import { colors, constants } from "utils";
import { SessionTrigger } from "../SessionTrigger";
import { OKOFooter } from "../../../OKOFooter";
import logo from "assets/OKO_Core_Logotype2.svg";
import { DeviceSettingsIcon, MicIcon, VolumeIcon } from "./Icons";
import { DeviceDropdown } from "./DeviceDropdown/DeviceDropdown";
import { DeviceCheckUI } from "./DeviceCheckUI/DeviceCheckUI";
import { DeviceRow, MicColumn, SpeakerColumn, SettingsButton } from "./styles";
import { NoDeviceFound } from "./NoDeviceFound/NoDeviceFound";

// Read navigator.connection info (Network Information API)
const getNetworkInfo = () => {
  const conn = navigator.connection;
  return {
    networkType: conn?.effectiveType || null,
    networkRtt: conn?.rtt ?? null,
    networkDownlink: conn?.downlink ?? null,
  };
};

export const Join = ({ error, isLoading, setIsLoading, setError, isSpectator, isWaitingForSessionToStart, isSessionReady }) => {
  const { session, setIsStart } = useSessionContext();
  const { groupCode, isRecordSessions, modal: { openDeviceSettingsModal } } = useAppStateContext();
  const { authUserData } = useAuthUserDataContext();
  const { localTracks, activeAudioSinkId, setActiveAudioSinkId, retryTrackAcquisition } = useVideoContext();
  const { audioInputDevices, audioOutputDevices, videoInputDevices } = useDevices();
  const localAudioTrack = localTracks.find((track) => track.kind === "audio");
  const mediaStreamTrack = useMediaStreamTrack(localAudioTrack);
  const trackDeviceId = mediaStreamTrack?.getSettings().deviceId;
  const [selectedAudioInputId, setSelectedAudioInputId] = useState(
    () => window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY)
  );
  const localAudioInputDeviceId = trackDeviceId || selectedAudioInputId;
  const activityType = session?.sessionActivityType;
  const [isSpeakerPlaying, setIsSpeakerPlaying] = useState(false);
  const handleSpeakerPlayingChange = useCallback((playing) => setIsSpeakerPlaying(playing), []);
  const [hasMicInput, setHasMicInput] = useState(false);
  const handleSoundDetected = useCallback(() => setHasMicInput(true), []);

  const replaceAudioInputTrack = useCallback((newDeviceId) => {
    window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, newDeviceId);
    setSelectedAudioInputId(newDeviceId);
    if (localAudioTrack) {
      localAudioTrack.restart({ deviceId: { exact: newDeviceId } });
    } else {
      retryTrackAcquisition();
    }
  }, [localAudioTrack, retryTrackAcquisition]);

  // Auto-select newly connected audio input device
  const prevAudioInputIdsRef = useRef(null);
  const audioInputIds = useMemo(
    () => audioInputDevices.map((d) => d.deviceId),
    [audioInputDevices]
  );
  useEffect(() => {
    const prev = prevAudioInputIdsRef.current;
    prevAudioInputIdsRef.current = audioInputIds;
    if (!prev || prev.length === 0 || audioInputIds.length === 0) return;
    const newId = audioInputIds.find((id) => !prev.includes(id));
    if (newId) {
      replaceAudioInputTrack(newId);
    }
  }, [audioInputIds, replaceAudioInputTrack]);

  const showDeviceCheck = !isSpectator && activityType !== constants.CONSENSUS_ENUM;

  const { devicesReady, micReady, speakerReady, cameraReady } = useDeviceReadiness({
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    isRecordSessions,
  });

  // Sentry logging: log once per screen view
  const hasLoggedRef = useRef(false);
  useEffect(() => {
    if (!showDeviceCheck || hasLoggedRef.current) return;
    hasLoggedRef.current = true;

    const event = devicesReady ? "device_check_loaded" : "no_device_found_loaded";
    logLiveKit("Device check screen loaded", {
      level: LogLevel.INFO,
      event,
      sessionId: session?.id,
      userId: authUserData?.id,
      groupCode,
      micReady,
      speakerReady,
      cameraReady,
      isRecordSessions,
      audioInputCount: audioInputDevices.length,
      audioOutputCount: audioOutputDevices.length,
      videoInputCount: videoInputDevices.length,
      hasAudioTrack: !!localAudioTrack,
      ...getNetworkInfo(),
    });
  }, [showDeviceCheck, devicesReady, session?.id, authUserData?.id, groupCode, micReady, speakerReady, cameraReady, isRecordSessions, audioInputDevices.length, audioOutputDevices.length, videoInputDevices.length, localAudioTrack]);

  const handleDemoJoin = useCallback(() => {
    logLiveKit("Demo mode join", {
      level: LogLevel.INFO,
      event: "demo_mode_join",
      sessionId: session?.id,
      userId: authUserData?.id,
      groupCode,
      hasAudioTrack: !!localAudioTrack,
      micReady,
      speakerReady,
      cameraReady,
      isRecordSessions,
      audioInputCount: audioInputDevices.length,
      audioOutputCount: audioOutputDevices.length,
      videoInputCount: videoInputDevices.length,
      ...getNetworkInfo(),
    });
    setError(null);
    setIsStart(true);
  }, [setError, setIsStart, session?.id, authUserData?.id, groupCode, localAudioTrack, micReady, speakerReady, cameraReady, isRecordSessions, audioInputDevices.length, audioOutputDevices.length, videoInputDevices.length]);

  return (
    <div className="d-flex fullscreen flex-column justify-content-center" style={{ backgroundColor: colors.milkGlass500 }}>
      <img src={logo} alt="logo" style={{ position: "absolute", top: "24px", left: "24px", zIndex: 1000 }} />
      {showDeviceCheck && !devicesReady && (
        <>
          <TopRightBar>
            <DeviceRow>
              <MicColumn>
                <DeviceDropdown
                  icon={<MicIcon />}
                  devices={audioInputDevices}
                  selectedDeviceId={localAudioInputDeviceId}
                  onSelect={replaceAudioInputTrack}
                  fallbackLabel="No microphone found"
                  dropDown
                />
              </MicColumn>
              <SpeakerColumn>
                <DeviceDropdown
                  icon={<VolumeIcon />}
                  devices={audioOutputDevices}
                  selectedDeviceId={activeAudioSinkId}
                  onSelect={setActiveAudioSinkId}
                  fallbackLabel="No speaker found"
                  dropDown
                />
              </SpeakerColumn>
            </DeviceRow>
            <SettingsButton
              aria-label="Device settings"
              onClick={openDeviceSettingsModal}
            >
              <DeviceSettingsIcon />
            </SettingsButton>
          </TopRightBar>
          <NoDeviceFound onDemoJoin={handleDemoJoin} retryTrackAcquisition={retryTrackAcquisition} isSessionReady={isSessionReady} isRecordSessions={isRecordSessions} />
        </>
      )}
      {showDeviceCheck && devicesReady && (
        <ContentContainer>
          <DeviceCheckUI
            audioInputDevices={audioInputDevices}
            audioOutputDevices={audioOutputDevices}
            localAudioTrack={localAudioTrack}
            mediaStreamTrack={mediaStreamTrack}
            localAudioInputDeviceId={localAudioInputDeviceId}
            activeAudioSinkId={activeAudioSinkId}
            setActiveAudioSinkId={setActiveAudioSinkId}
            replaceAudioInputTrack={replaceAudioInputTrack}
            isSpeakerPlaying={isSpeakerPlaying}
            handleSpeakerPlayingChange={handleSpeakerPlayingChange}
            onSoundDetected={handleSoundDetected}
          />
          {isLoading && isWaitingForSessionToStart && (
            <p style={{ textAlign: "center" }}>Please wait for the session to start!</p>
          )}
          <CtaSection>
            <ReadyHeading>Today's Goal: Talk it out and reach Teamwork Level 4!</ReadyHeading>
            <SessionTrigger
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setError={setError}
              isSpectator={isSpectator}
              isSessionReady={isSessionReady}
              hasDevices={!!localAudioTrack}
              hasMicInput={hasMicInput}
            />
          </CtaSection>
          {error && (
            <Alert
              style={{ overflowWrap: "break-word" }}
              dismissible
              onClose={() => setError(null)}
              variant="danger"
            >
              {error}
            </Alert>
          )}
        </ContentContainer>
      )}
      <OKOFooter />
    </div>
  );
};

const TopRightBar = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  max-width: 544px;
  width: 100%;
  z-index: 1000;

  ${() => DeviceRow} {
    flex: 1;
    max-width: none;
  }
`;

const ContentContainer = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1024px;
  width: 100%;

  @media (max-width: 1024px), (max-height: 600px) {
    max-width: 800px;
  }
`;

const CtaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReadyHeading = styled.h1`
  color: #000;
  font-family: 'ABC Marfa', sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: 0;
  margin: 0;
`;
